import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Navbar } from '../components/layout/Navbar';
import { apiFetch } from '../lib/apiClient';
import { 
  ChevronRight, 
  ChevronLeft, 
  Sparkles, 
  Palette,
  Clock,
  Target,
  Star,
  CheckCircle2,
  Layers
} from 'lucide-react';

export default function QuestionnairePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    experienceLevel: '',
    practiceFrequency: [],
    goals: [],
    goalsInput: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkProfile = async () => {
      try {
        const response = await apiFetch('/auth/profile', {
          method: 'GET',
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.message || data?.error || 'Unable to load profile');
        }

        const questionnaire =
          data?.profile?.questionnaire || data?.questionnaire || {};

        if (questionnaire && questionnaire.completedAt) {
          if (isMounted) {
            navigate('/dashboard/onboarding/upload', { replace: true });
          }
          return;
        }
      } catch {
        // If profile fetch fails, allow user to proceed with questionnaire.
      } finally {
        if (isMounted) {
          setIsCheckingProfile(false);
        }
      }
    };

    checkProfile();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const steps = [
    {
      id: 'experienceLevel',
      title: 'Your Experience Level',
      description: 'Help us understand where you are in your art journey',
      icon: Star,
      type: 'single',
      question: 'What best describes your current experience level?',
      options: [
        { value: 'Beginner', label: 'Beginner', desc: 'You are just getting started with drawing.' },
        { value: 'Intermediate', label: 'Intermediate', desc: 'You have foundations and want to level up.' },
        { value: 'Advanced', label: 'Advanced', desc: 'You draw confidently and want to refine your skills.' },
        { value: 'Professional', label: 'Professional', desc: 'You work with clients or in a studio setting.' }
      ]
    },
    {
      id: 'practiceFrequency',
      title: 'How Often Do You Practice?',
      description: 'Tell us how frequently you can realistically draw.',
      icon: Clock,
      type: 'multiple',
      question: 'How often do you typically practice art?',
      options: [
        { value: 'Occasionally', label: 'Occasionally', desc: 'A few times a month when inspiration hits.' },
        { value: 'Weekly', label: 'A few times a week', desc: '1–3 focused sessions each week.' },
        { value: 'Daily', label: 'Daily', desc: 'You draw almost every day.' },
        { value: 'Intensive', label: 'Intensive', desc: 'Multiple sessions per day or long study blocks.' }
      ]
    },
    {
      id: 'goals',
      title: 'Your Goals',
      description: 'Share the specific goals you want SkillSkulpt to support.',
      icon: Target,
      type: 'text',
      question: 'What are your top 1–3 art goals?',
      placeholder:
        'E.g., To get better at drawing human features\nTo master the realism art style\nTo improve my portfolio for a job application \n(Enter each goal on a new line)'
    }
  ];

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleSingleSelect = (value) => {
    setAnswers(prev => ({
      ...prev,
      [currentStepData.id]: value
    }));
  };

  const handleMultipleSelect = (value, checked) => {
    setAnswers(prev => ({
      ...prev,
      [currentStepData.id]: checked
        ? value === 'Daily'
          ? ['Daily']
          : [...(prev[currentStepData.id] || []).filter(v => v !== 'Daily'), value]
        : (prev[currentStepData.id] || []).filter(v => v !== value)
    }));
  };

  const handleTextChange = (value) => {
    setAnswers(prev => ({
      ...prev,
      goalsInput: value,
      goals: value
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
    }));
  };

  const canProceed = () => {
    if (currentStepData.type === 'single') {
      return !!answers[currentStepData.id];
    }
    if (currentStepData.type === 'multiple') {
      return (answers[currentStepData.id] || []).length > 0;
    }
    if (currentStepData.type === 'text') {
      return (answers.goals || []).length > 0;
    }
    return true;
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      return;
    }

    setError('');
    setIsSubmitting(true);

    const payload = {
      experienceLevel: answers.experienceLevel,
      practiceFrequency: (answers.practiceFrequency || [])[0] || '',
      goals: answers.goals || [],
    };

    try {
      const response = await apiFetch('/diagnostic/questionnaire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data?.message || data?.error || 'Unable to save questionnaire. Please try again.';
        throw new Error(message);
      }

      navigate('/dashboard/onboarding/upload');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (isCheckingProfile) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)] flex flex-col" data-testid="questionnaire-loading">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center p-8 mt-12 animate-in fade-in duration-500">
          <div className="relative w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-[hsl(262,83%,58%)]/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-[hsl(262,83%,58%)] border-b-transparent animate-spin duration-1000"></div>
            <div className="absolute inset-0 flex items-center justify-center bg-white rounded-full m-2 shadow-sm">
              <Sparkles className="w-8 h-8 text-[hsl(262,83%,58%)] animate-pulse" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit'] mb-2">
            Preparing onboarding
          </h2>
          <p className="text-slate-500 max-w-sm text-center animate-pulse">
            Checking your profile and assessment status...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="questionnaire-page">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Progress Header */}
          <div className="mb-8" data-testid="progress-header">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-600">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-sm font-medium text-[hsl(234,89%,63%)]">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-center gap-2 mb-8" data-testid="step-indicators">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                onClick={() => idx < currentStep && setCurrentStep(idx)}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 text-sm font-medium ${
                  idx < currentStep
                    ? 'bg-[hsl(234,89%,63%)] text-white cursor-pointer'
                    : idx === currentStep
                    ? 'bg-white border-2 border-[hsl(234,89%,63%)] text-[hsl(234,89%,63%)] shadow-sm'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
                disabled={idx > currentStep}
                data-testid={`step-indicator-${idx + 1}`}
              >
                {idx < currentStep ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <span>{idx + 1}</span>
                )}
              </button>
            ))}
          </div>

          {/* Question Card */}
          <Card className="card-elevated border-0 animate-fade-in" data-testid="question-card">
            <CardHeader className="text-center pb-2">
              <div className="w-12 h-12 rounded-xl bg-[hsl(234,89%,63%)]/10 flex items-center justify-center mx-auto mb-4">
                <currentStepData.icon className="w-6 h-6 text-[hsl(234,89%,63%)]" />
              </div>
              <CardTitle className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit']">
                {currentStepData.title}
              </CardTitle>
              <CardDescription className="text-slate-600">
                {currentStepData.description}
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-6">
              <p className="text-lg font-medium text-[hsl(222,47%,11%)] mb-6 text-center">
                {currentStepData.question}
              </p>

              {/* Single Select */}
              {currentStepData.type === 'single' && (
                <RadioGroup
                  value={answers[currentStepData.id] || ''}
                  onValueChange={handleSingleSelect}
                  className="grid gap-3"
                  data-testid="single-select-group"
                >
                  {currentStepData.options.map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={option.value}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={option.value}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          answers[currentStepData.id] === option.value
                            ? 'border-[hsl(234,89%,63%)] bg-[hsl(234,89%,63%)]/5'
                            : 'border-slate-200 hover:border-[hsl(234,89%,63%)]/40'
                        }`}
                        data-testid={`option-${option.value}`}
                      >
                        {option.emoji && (
                          <span className="text-2xl">{option.emoji}</span>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-[hsl(222,47%,11%)]">{option.label}</p>
                          <p className="text-sm text-slate-500">{option.desc}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          answers[currentStepData.id] === option.value
                            ? 'border-[hsl(234,89%,63%)] bg-[hsl(234,89%,63%)]'
                            : 'border-slate-300'
                        }`}>
                          {answers[currentStepData.id] === option.value && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {/* Multiple Select */}
              {currentStepData.type === 'multiple' && (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="multiple-select-group">
                  {currentStepData.options.map((option) => {
                    const isChecked = (answers[currentStepData.id] || []).includes(option.value);
                    return (
                      <div
                        key={option.value}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isChecked
                            ? 'border-[hsl(234,89%,63%)] bg-[hsl(234,89%,63%)]/5'
                            : 'border-slate-200 hover:border-[hsl(234,89%,63%)]/40'
                        }`}
                        onClick={() => handleMultipleSelect(option.value, !isChecked)}
                        data-testid={`option-${option.value}`}
                      >
                        <Checkbox
                          id={option.value}
                          checked={isChecked}
                          onCheckedChange={(checked) => handleMultipleSelect(option.value, checked)}
                          className="data-[state=checked]:bg-[hsl(234,89%,63%)] data-[state=checked]:border-[hsl(234,89%,63%)]"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-[hsl(222,47%,11%)] text-sm">{option.label}</p>
                          <p className="text-xs text-slate-500">{option.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Text Input */}
              {currentStepData.type === 'text' && (
                <Textarea
                  value={answers.goalsInput}
                  onChange={(e) => handleTextChange(e.target.value)}
                  placeholder={currentStepData.placeholder}
                  className="min-h-[120px] border-slate-200 focus:border-[hsl(234,89%,63%)] focus:ring-[hsl(234,89%,63%)]/20 resize-none"
                  data-testid="text-input"
                />
              )}
            </CardContent>

            {/* Navigation */}
            <div className="px-6 pb-6 pt-4 flex items-center justify-between border-t border-slate-100 mt-6">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="text-slate-600 hover:text-[hsl(222,47%,11%)]"
                data-testid="back-button"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>

              <Button
                onClick={handleNext}
                disabled={isSubmitting || (!canProceed() && currentStepData.type !== 'text')}
                className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full px-6 disabled:opacity-60"
                data-testid="next-button"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Saving...' : 'Complete Assessment'}
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Helper Text */}
          <div className="mt-6 text-center text-sm">
            <p className="text-slate-500">
              Your answers help us create a personalized curriculum tailored to your goals.
            </p>
            {error && (
              <p className="text-red-500 mt-2" data-testid="questionnaire-error">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
